<template>
  <div>
    <v-tabs v-model="tab" class="mb-4" :disabled="busy || adding || !!pending || specialBusy">
      <v-tab :disabled="specialBusy">ผู้มีสิทธิ์ตั้งค่าตารางเรียน</v-tab>
      <v-tab :disabled="busy || adding || !!pending">ช่วงเวลาพิเศษ</v-tab>
    </v-tabs>
    <config-schedule-special-period v-if="tab === 1" :faculty-rows="facultyRows" @busy="specialBusy = $event" />
    <div v-show="tab === 0">
  <v-card outlined :loading="loading || saving">
    <v-card-title class="text-h6">
      <v-icon color="primary" class="mr-2">mdi-account-key</v-icon>
      ผู้มีสิทธิ์ตั้งค่าตารางเรียน
      <v-spacer />
      <v-btn text :disabled="busy" @click="reload"><v-icon left>mdi-refresh</v-icon>โหลดใหม่</v-btn>
    </v-card-title>
    <v-card-subtitle>มอบหมายผู้รับผิดชอบแยกตามระดับการศึกษาและคณะ</v-card-subtitle>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="4">
          <v-select v-model="level" :items="levels" label="ระดับการศึกษา" outlined dense hide-details :disabled="busy" />
        </v-col>
        <v-col cols="12" md="8">
          <v-autocomplete v-model="faculty" :items="faculties" item-text="text" item-value="value"
            label="คณะ" outlined dense hide-details :disabled="busy" />
        </v-col>
      </v-row>
      <v-row class="mt-2" align="center">
        <v-col cols="12" sm="8">
          <v-text-field v-model="search" label="ค้นหาชื่อ นามสกุล หรือบัญชีผู้ใช้" prepend-inner-icon="mdi-magnify"
            outlined dense clearable hide-details />
        </v-col>
        <v-col cols="12" sm="4" class="text-sm-right">
          <v-btn color="primary" :disabled="busy || !faculty || !ready" @click="openAdd">
            <v-icon left>mdi-account-multiple-plus</v-icon>เพิ่มผู้มีสิทธิ์
          </v-btn>
        </v-col>
      </v-row>
      <div class="mt-4 mb-2">{{ facultyLabel }} · {{ scopedRows.length }} รายการ
        <v-chip small class="ml-2" color="success" outlined>เปิดใช้งาน {{ activeCount }}</v-chip>
      </div>
      <v-data-table :headers="headers" :items="scopedRows" :search="search" :loading="loading"
        item-key="ID" :items-per-page="10" :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'แสดงต่อหน้า' }"
        no-data-text="ยังไม่มีผู้ได้รับสิทธิ์ในขอบเขตนี้" no-results-text="ไม่พบรายชื่อที่ค้นหา" loading-text="กำลังโหลดข้อมูล">
        <template v-slot:[`item.name`]="{ item }">
          <div class="py-2 font-weight-medium">{{ item.name }}</div>
          <div v-if="!Number(item.ELIGIBLE)" class="caption grey--text text--darken-1 pb-2">
            สถานะบุคลากรหรือสิทธิ์บัญชีปัจจุบันไม่ตรงตามเงื่อนไข
          </div>
        </template>
        <template v-slot:[`item.ACTIVE`]="{ item }">
          <v-chip small :color="Number(item.ACTIVE) ? 'success' : 'grey'" outlined>
            {{ Number(item.ACTIVE) ? 'เปิดใช้งาน' : 'ปิดใช้งาน' }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small text :disabled="busy || !ready || (!Number(item.ACTIVE) && !Number(item.ELIGIBLE))"
            @click="askChange(item, Number(item.ACTIVE) ? 'disable' : 'enable')">
            {{ Number(item.ACTIVE) ? 'ปิดสิทธิ์' : 'เปิดสิทธิ์' }}
          </v-btn>
          <v-btn small text color="error" :disabled="busy || !ready" @click="askChange(item, 'delete')">ถอนสิทธิ์</v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="adding" max-width="760" persistent>
      <v-card>
        <v-card-title>เพิ่มผู้มีสิทธิ์ตั้งค่าตารางเรียน</v-card-title>
        <v-card-text>
          <p>ระดับ{{ levelLabel }} · คณะ: {{ facultyLabel }}</p>
          <v-autocomplete v-model="selected" :items="employeeOptions" item-text="label" item-value="USER_ID"
            label="เลือกบุคลากร (เลือกได้หลายคน)" hint="เลือกบุคลากรที่มีสิทธิ์ใช้งานระบบได้จากทุกคณะต้นสังกัด"
            persistent-hint multiple chips deletable-chips outlined :disabled="saving" :search-input.sync="employeeSearch"
            no-data-text="ไม่พบบุคลากรที่ตรงกับคำค้น" @change="employeeSearch = ''">
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.disabled">มีรายการสิทธิ์ในขอบเขตนี้แล้ว</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div class="mt-5 font-weight-medium">ตรวจสอบรายชื่อก่อนบันทึก · เลือก {{ selected.length }} คน</div>
          <v-list dense aria-label="รายชื่อที่เลือก">
            <v-list-item v-for="user in selectedEmployees" :key="user.USER_ID">
              <v-list-item-icon><v-icon>mdi-account-check-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title class="text-wrap">{{ user.label }}</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="adding = false">ยกเลิก</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!selected.length || selected.length > 500" @click="saveAdd">
            บันทึก {{ selected.length }} คน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!pending" max-width="520" persistent>
      <v-card v-if="pending">
        <v-card-title>{{ actionLabel }}ตั้งค่าตารางเรียน</v-card-title>
        <v-card-text>
          <p class="font-weight-medium">{{ pending.item.name }} ({{ pending.item.USER_ID }})</p>
          <p>ระดับ{{ levelLabel }} · {{ facultyLabel }}</p>
          <p v-if="pending.action === 'delete'">ถอนรายการสิทธิ์นี้ หากต้องการมอบสิทธิ์อีกครั้งสามารถเพิ่มใหม่ได้</p>
          <p v-else-if="pending.action === 'disable'">ปิดการใช้งานรายการนี้ โดยเก็บรายชื่อไว้เพื่อเปิดสิทธิ์ภายหลัง</p>
          <p v-if="pending.action !== 'enable'">สิทธิ์อื่นของบุคลากรคนนี้ยังมีผล เช่น สิทธิ์ทุกคณะหรือสิทธิ์ส่วนกลาง</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="pending = null">ยกเลิก</v-btn>
          <v-btn :color="pending.action === 'enable' ? 'primary' : 'error'" :loading="saving" @click="saveChange">ยืนยัน{{ actionLabel }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
    </div>
  </div>
</template>

<script>
import SchedulePermissionProvider from '@/services/schedule_permission.provider';
import GenericProvider from '@/services/generic.provider';
import ConfigScheduleSpecialPeriod from './ConfigScheduleSpecialPeriod.vue';
const service = new SchedulePermissionProvider();
const generic = new GenericProvider();

export default {
  name: 'ConfigSchedulePermission',
  components: { ConfigScheduleSpecialPeriod },
  data: () => ({
    tab: 0, specialBusy: false,
    loading: false, saving: false, ready: false, employeesLoaded: false, loadVersion: 0,
    level: '1', faculty: null, search: '', employeeSearch: '', rows: [], employees: [], facultyRows: [],
    adding: false, selected: [], pending: null,
    levels: [{ text: 'ปริญญาตรี', value: '1' }, { text: 'บัณฑิตศึกษา', value: '2' }],
    headers: [
      { text: 'ชื่อ–นามสกุล', value: 'name' }, { text: 'บัญชีผู้ใช้', value: 'USER_ID' },
      { text: 'สถานะสิทธิ์', value: 'ACTIVE' }, { text: 'จัดการ', value: 'actions', sortable: false, filterable: false },
    ],
  }),
  computed: {
    busy() { return this.loading || this.saving; },
    levelLabel() { return this.levels.find(x => x.value === this.level).text; },
    faculties() {
      return [{ value: 'AL', text: 'ทุกคณะ' }, ...this.facultyRows.map(x => ({
        value: String(x.faculty_id), text: x.faculty_name_th || x.faculty_name || String(x.faculty_id),
      }))];
    },
    facultyLabel() { return (this.faculties.find(x => x.value === this.faculty) || {}).text || 'กรุณาเลือกคณะ'; },
    scopedRows() {
      return this.rows.map(x => ({ ...x, name: this.personName(x) }));
    },
    activeCount() { return this.scopedRows.filter(x => Number(x.ACTIVE)).length; },
    employeeOptions() {
      const existing = new Set(this.scopedRows.map(x => x.USER_ID));
      return this.employees.map(x => ({ ...x, label: `${this.personName(x)} (${x.USER_ID})`, disabled: existing.has(x.USER_ID) }));
    },
    selectedEmployees() { return this.employeeOptions.filter(x => this.selected.includes(x.USER_ID)); },
    actionLabel() { return this.pending ? { enable: 'เปิดสิทธิ์', disable: 'ปิดสิทธิ์', delete: 'ถอนสิทธิ์' }[this.pending.action] : ''; },
  },
  watch: {
    level() { return this.scopeChanged(); },
    faculty() { return this.scopeChanged(); },
  },
  mounted() { this.reload(); },
  methods: {
    personName(person) {
      return [person.PREFIX_TH || person.PREFIX_EN, person.FULLNAME_TH || person.FULLNAME_EN].filter(Boolean).join(' ') || person.USER_ID;
    },
    errorMessage(error) {
      const message = error.response && error.response.data && error.response.data.message;
      return typeof message === 'string' ? message : 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่';
    },
    async reload(permissionsOnly = false) {
      const version = ++this.loadVersion;
      const level = this.level;
      const faculty = this.faculty;
      this.loading = true; this.ready = false;
      this.rows = [];
      try {
        const grants = faculty ? service.permissions(level, faculty) : Promise.resolve([]);
        if (permissionsOnly === true) {
          const rows = await grants;
          if (version !== this.loadVersion) return;
          this.rows = rows;
        } else {
          this.employeesLoaded = false;
          const [rows, faculties] = await Promise.all([grants, generic.getRegistrarFaculty()]);
          if (version !== this.loadVersion) return;
          this.rows = rows; this.facultyRows = faculties;
        }
        this.ready = true;
      } catch (error) {
        if (version === this.loadVersion) this.$toasted.error(this.errorMessage(error));
      } finally { if (version === this.loadVersion) this.loading = false; }
    },
    scopeChanged() {
      this.search = ''; this.selected = [];
      return this.reload(true);
    },
    async openAdd() {
      if (this.busy) return;
      this.selected = []; this.employeeSearch = '';
      if (!this.employeesLoaded) {
        this.loading = true;
        try {
          this.employees = await service.employees();
          this.employeesLoaded = true;
        } catch (error) {
          this.$toasted.error(this.errorMessage(error));
          return;
        } finally { this.loading = false; }
      }
      this.adding = true;
    },
    askChange(item, action) { this.pending = { item, action }; },
    async saveAdd() {
      this.saving = true;
      try {
        const result = await service.add(this.level, this.faculty, this.selected);
        this.adding = false;
        this.$toasted.success(`เพิ่มสิทธิ์ ${result.added} คน${result.skipped ? ` · มีรายการอยู่แล้ว ${result.skipped} คน` : ''}`);
        await this.reload(true);
      } catch (error) { this.$toasted.error(this.errorMessage(error)); }
      finally { this.saving = false; }
    },
    async saveChange() {
      this.saving = true;
      try {
        const { item, action } = this.pending;
        await service.change(item.ID, action);
        if (action === 'delete') {
          this.rows = this.rows.filter(row => String(row.ID) !== String(item.ID));
        } else {
          this.rows = this.rows.map(row => String(row.ID) === String(item.ID)
            ? { ...row, ACTIVE: action === 'enable' ? 1 : 0 } : row);
        }
        this.$toasted.success(`${this.actionLabel}เรียบร้อยแล้ว`);
        this.pending = null;
      } catch (error) { this.$toasted.error(this.errorMessage(error)); }
      finally { this.saving = false; }
    },
  },
};
</script>
